import React from "react";
import { fetchDepEditorGraph } from "../../../../api/clientV2";
import type {
  DepGraph,
  DepGraphPredType,
  DraftPayload,
} from "../../../../models";
import { addEdge, removeEdge, retypeEdge, wouldCreateCycle } from "../../depCore/edgeIntents";
import { notify } from "../../../../components/Notice/noticeStore";
import type { BarSpan } from "../../WbsBoardView/WbsBoardView";
import type { ActivityInstance } from "../../draft/types";
import { computeBoardReflect } from "../boardReflect";
import type { PaneMode } from "../../UnifiedBoard/UnifiedBoard";
import type { GridView } from "./useViewState";
import type { ProtoNewAct } from "../PullPlanGrid";

interface DepGraphArgs {
  scheduleId: number;
  depEditorSection?: string;
  readOnly: boolean;
  effectiveDraft: DraftPayload;
  /** View-state gating (from useViewState). */
  view: GridView;
  paneMode: PaneMode;
  seqHold: boolean;
  /** Workday axis for repositionFromGraph. */
  calendar: string[];
  /** Proto-window-relative column lookup for reflectGraphDatesToBoard. */
  protoCalendarIndexFor: (iso: string | null) => number;
  protoNewActs: ProtoNewAct[];
  pushUndo: (label?: string) => void;
  setPendingOps: React.Dispatch<React.SetStateAction<DraftPayload>>;
  setInstances: React.Dispatch<React.SetStateAction<ActivityInstance[]>>;
  setProtoSpans: React.Dispatch<React.SetStateAction<Map<number, BarSpan>>>;
  setProtoNewActs: React.Dispatch<React.SetStateAction<ProtoNewAct[]>>;
}

interface DepGraphApi {
  depGraph: DepGraph | null;
  setDepGraph: React.Dispatch<React.SetStateAction<DepGraph | null>>;
  depGraphLoading: boolean;
  depGraphError: string | null;
  depGraphRef: React.MutableRefObject<DepGraph | null>;
  refetchDepGraph: (immediate: boolean) => void;
  repositionFromGraph: () => void;
  reflectGraphDatesToBoard: () => void;
  /** Set true by an Apply before its refetch; the deferred reflect effect
   *  consumes it once the new graph lands. */
  pendingApplyDatesRef: React.MutableRefObject<boolean>;
  /** Set true by a board-side logic edit so the next graph snaps the board even
   *  when the gantt isn't the active pane. */
  pendingBoardRepositionRef: React.MutableRefObject<boolean>;
  handleAddEdge: (predId: string, succId: string) => void;
  handleRemoveEdge: (succId: string, predId: string) => void;
  handleRetypeEdge: (
    succId: string,
    predId: string,
    type: DepGraphPredType,
    lagDays: number,
  ) => void;
  handleReplaceEdge: (
    removeSucc: string,
    removePred: string,
    addPred: string,
    addSucc: string,
    type: DepGraphPredType,
    lagDays: number,
  ) => void;
}

/** Owns the unified dep-editor graph — the flat, already-cascaded scheduled
 *  graph the backend builds from the live committed schedule + this draft's
 *  pending_* ops — its (debounced) refetch lifecycle, the board reflect/
 *  reposition bridges, and the four edge-edit intents. */
export function useDepGraph({
  scheduleId,
  depEditorSection,
  readOnly,
  effectiveDraft,
  view,
  paneMode,
  seqHold,
  calendar,
  protoCalendarIndexFor,
  protoNewActs,
  pushUndo,
  setPendingOps,
  setInstances,
  setProtoSpans,
  setProtoNewActs,
}: DepGraphArgs): DepGraphApi {
  const [depGraph, setDepGraph] = React.useState<DepGraph | null>(null);
  const [depGraphLoading, setDepGraphLoading] = React.useState(false);
  const [depGraphError, setDepGraphError] = React.useState<string | null>(null);
  const depGraphRef = React.useRef<DepGraph | null>(null);
  depGraphRef.current = depGraph;
  const depFetchTimer = React.useRef<number | null>(null);
  const depFetchSeq = React.useRef(0);

  // Fetch (debounced) the unified scheduled graph: on dep-editor open and after
  // every staged edit. The host owns the draft; the backend assembles the flat
  // graph (committed schedule + draft pending_* ops) and cascades dates. The
  // modal is purely presentational — it derives rows / bars / arrows from this.
  const refetchDepGraph = React.useCallback(
    (immediate: boolean) => {
      if (!depEditorSection) return;
      if (depFetchTimer.current != null) window.clearTimeout(depFetchTimer.current);
      const run = () => {
        const seq = ++depFetchSeq.current;
        setDepGraphError(null);
        if (depGraphRef.current == null) setDepGraphLoading(true);
        fetchDepEditorGraph(scheduleId, depEditorSection, effectiveDraft)
          .then((graph) => {
            if (seq !== depFetchSeq.current) return;
            setDepGraph(graph);
          })
          .catch((e: any) => {
            if (seq !== depFetchSeq.current) return;
            setDepGraphError(
              e?.response?.data?.error ?? e?.message ?? "Failed to load dependencies",
            );
          })
          .finally(() => {
            if (seq === depFetchSeq.current) setDepGraphLoading(false);
          });
      };
      if (immediate) run();
      else depFetchTimer.current = window.setTimeout(run, 250);
    },
    [scheduleId, depEditorSection, effectiveDraft],
  );

  // The gantt is "active" (its forward-pass graph should be fetched/kept fresh)
  // whenever the gantt pane is visible — gantt-only OR Both. The board only
  // AUTO-SNAPS to those dates when it's NOT shown alongside (gantt-only): in
  // Both, the board stays hand-placed and "Apply dates to board" is the explicit
  // gate. (Anchors is its own tab and uses the graph too.)
  const graphActive =
    view === "anchors" ? Boolean(depEditorSection) : paneMode !== "board" && Boolean(depEditorSection);
  const boardSnapActive = view !== "anchors" && paneMode === "gantt" && Boolean(depEditorSection);
  React.useEffect(() => {
    if (!graphActive || seqHold) return;
    refetchDepGraph(false);
    return () => {
      if (depFetchTimer.current != null) window.clearTimeout(depFetchTimer.current);
    };
  }, [graphActive, seqHold, refetchDepGraph]);

  // Edit intents — each restages the draft's pending_dep_edits against the
  // current graph; the effect above re-fetches the cascaded graph.
  const handleAddEdge = React.useCallback(
    (predId: string, succId: string) => {
      const g = depGraphRef.current;
      if (!g) return;
      if (wouldCreateCycle(g, predId, succId)) {
        notify(
          "Can't add this tie — it would create a dependency loop (the predecessor already depends on this activity).",
          "error",
        );
        return;
      }
      pushUndo("Tie add");
      setPendingOps((prev) => ({
        ...prev,
        pending_dep_edits: addEdge(prev.pending_dep_edits, g, predId, succId),
      }));
    },
    [pushUndo, setPendingOps],
  );

  const handleRemoveEdge = React.useCallback(
    (succId: string, predId: string) => {
      const g = depGraphRef.current;
      if (!g) return;
      pushUndo();
      setPendingOps((prev) => ({
        ...prev,
        pending_dep_edits: removeEdge(prev.pending_dep_edits, g, succId, predId),
      }));
    },
    [pushUndo, setPendingOps],
  );

  const handleRetypeEdge = React.useCallback(
    (succId: string, predId: string, type: DepGraphPredType, lagDays: number) => {
      const g = depGraphRef.current;
      if (!g) return;
      pushUndo();
      setPendingOps((prev) => ({
        ...prev,
        pending_dep_edits: retypeEdge(prev.pending_dep_edits, g, succId, predId, type, lagDays),
      }));
    },
    [pushUndo, setPendingOps],
  );

  // Re-point a tie atomically: drop the old edge and add the new one (with its
  // type / lag) in ONE pending-ops update, so the replace can never leave the
  // old edge behind (the failure mode of staging remove + add separately).
  const handleReplaceEdge = React.useCallback(
    (
      removeSucc: string,
      removePred: string,
      addPred: string,
      addSucc: string,
      type: DepGraphPredType,
      lagDays: number,
    ) => {
      const g = depGraphRef.current;
      if (!g) return;
      if (wouldCreateCycle(g, addPred, addSucc)) {
        notify("Can't move this tie there — it would create a dependency loop.", "error");
        return;
      }
      pushUndo("Re-point tie");
      setPendingOps((prev) => {
        let edits = removeEdge(prev.pending_dep_edits, g, removeSucc, removePred);
        edits = addEdge(edits, g, addPred, addSucc, type, lagDays);
        return { ...prev, pending_dep_edits: edits };
      });
    },
    [pushUndo, setPendingOps],
  );

  // Snap every bar to the cascaded start the dep-editor graph computed, so the
  // grid reflects the dependency edits' new dates (purely visual — start_index
  // doesn't feed the draft, which anchors/cascades server-side). A cascaded date
  // off the grid's workday axis snaps to the prior column.
  const repositionFromGraph = React.useCallback(() => {
    const g = depGraphRef.current;
    if (!g) return;
    const dayIndex = new Map(calendar.map((iso, i) => [iso, i]));
    const idxFor = (iso: string): number | null => {
      const exact = dayIndex.get(iso);
      if (exact != null) return exact;
      const next = calendar.findIndex((d) => d >= iso);
      if (next === -1) return calendar.length ? calendar.length - 1 : null;
      return next === 0 ? 0 : next - 1;
    };
    setInstances((prev) => {
      let changed = false;
      const next = prev.map((inst) => {
        const gid =
          inst.source_activity_id != null ? String(inst.source_activity_id) : inst.draftId;
        if (!gid) return inst;
        const node = g.activities[gid];
        // Snap to the retained-early (drive) start — for an active activity
        // `start` is its actual start, which the board never plans against.
        if (!node || !node.drive_start) return inst;
        const idx = idxFor(node.drive_start.slice(0, 10));
        if (idx == null || idx === inst.start_index) return inst;
        changed = true;
        return { ...inst, start_index: idx };
      });
      return changed ? next : prev;
    });
  }, [calendar, setInstances]);

  // One graph fetch when the workspace opens in plan mode, so the Anchors
  // badge is live even before the user leaves the Board.
  const initialFetchRef = React.useRef(false);
  React.useEffect(() => {
    if (readOnly || !depEditorSection || initialFetchRef.current) return;
    initialFetchRef.current = true;
    refetchDepGraph(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, depEditorSection]);

  // Board-side ops that change the section's anchors or ties (deletes, tie
  // edits, adds) refresh the graph too, so the badge, publish warning, and
  // the bar popover's driving pred stay honest without a tab switch. The
  // dep-edit part keys by CONTENT — an in-place retype changes no counts.
  // Bar drags still don't refire this (spans aren't in the key).
  const refetchDepGraphRef = React.useRef(refetchDepGraph);
  refetchDepGraphRef.current = refetchDepGraph;
  const anchorOpsKey = `${effectiveDraft.pending_activity_deletes?.length ?? 0}|${
    JSON.stringify(effectiveDraft.pending_dep_edits ?? [])
  }|${effectiveDraft.pending_new_activities?.length ?? 0}`;
  const prevAnchorOpsKey = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (prevAnchorOpsKey.current === anchorOpsKey) return;
    const first = prevAnchorOpsKey.current === null;
    prevAnchorOpsKey.current = anchorOpsKey;
    // First run is the mount — the initial-fetch effect above already loads.
    if (first || readOnly || graphActive) return;
    refetchDepGraphRef.current(false);
  }, [anchorOpsKey, readOnly, graphActive]);

  // Keep the board's bars snapped to the cascaded dates the sequence view
  // computes, so flipping Sequence → Board always shows the live re-plan.
  // Board-side logic edits (Link in order) set the pending flag so the SAME
  // snap runs there the moment the recalculated graph lands — no tab switch.
  // Idempotent: reposition only writes instances whose start actually moved.
  const pendingBoardRepositionRef = React.useRef(false);
  React.useEffect(() => {
    if (depGraph == null) return;
    if (!boardSnapActive && !pendingBoardRepositionRef.current) return;
    pendingBoardRepositionRef.current = false;
    repositionFromGraph();
  }, [boardSnapActive, depGraph, repositionFromGraph]);

  // After an Apply stages its tie/duration edits, the graph refetches with the
  // new logic; when it lands we snap the committed board bars (protoSpans) onto
  // those dates so the edit shows on the board immediately — no manual "apply
  // dates" click, no tab switch.
  const pendingApplyDatesRef = React.useRef(false);

  // Write the forward-pass graph's dates onto the board bars (committed spans +
  // new-activity rows). Pure reflect — no undo push, no view switch — so it can
  // run both from the manual button and automatically after an Apply lands.
  const reflectGraphDatesToBoard = React.useCallback(() => {
    const g = depGraphRef.current;
    if (!g) return;
    const { spanPatches, newPatches } = computeBoardReflect(
      g,
      protoCalendarIndexFor,
      protoNewActs.map((n) => n.draftId),
    );
    if (spanPatches.size > 0) {
      setProtoSpans((prev) => {
        const next = new Map(prev);
        spanPatches.forEach((v, k) => next.set(k, v));
        return next;
      });
    }
    if (newPatches.size > 0) {
      setProtoNewActs((prev) =>
        prev.map((n, i) => {
          const p = newPatches.get(i);
          return p ? { ...n, startIndex: p.startIndex, durationDays: p.durationDays } : n;
        }),
      );
    }
  }, [protoCalendarIndexFor, protoNewActs, setProtoSpans, setProtoNewActs]);

  // The deferred board-reflect: fires once the post-Apply graph refetch lands.
  React.useEffect(() => {
    if (depGraph == null || !pendingApplyDatesRef.current) return;
    pendingApplyDatesRef.current = false;
    reflectGraphDatesToBoard();
  }, [depGraph, reflectGraphDatesToBoard]);

  return {
    depGraph,
    setDepGraph,
    depGraphLoading,
    depGraphError,
    depGraphRef,
    refetchDepGraph,
    repositionFromGraph,
    reflectGraphDatesToBoard,
    pendingApplyDatesRef,
    pendingBoardRepositionRef,
    handleAddEdge,
    handleRemoveEdge,
    handleRetypeEdge,
    handleReplaceEdge,
  };
}
